import { Location } from "@angular/common";
import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors, AsyncValidatorFn } from "@angular/forms";
import { ProDefiniciones } from "@shared/interfaces/request/proDefiniciones";

import { ConceptosService } from "@p-p-portafolio/services/conceptos.service";
import { ProDefinicionesService } from "@p-p-portafolio/services/pro-definiciones.service";
import { TasasService } from "@p-p-portafolio/services/tasas.service";
import { UserService } from "@pages/user/services/user.service";
import { InjectableService } from "@shared/utils/injectable.service";
import { SharedDataService } from "@shared/utils/shared-data.service";
import { SwalService } from "@shared/utils/swal.service";
import { Observable, of, Subscription } from "rxjs";
import { map } from "rxjs/operators";

export interface IDefGeneral {
    promocion: string;
    numVar: number;
    descripcion: string;
    claveTasa: string;
    valorFijo: number;
    amortInf: number;
    amortSup: number;
    formula: string;
    porcentaje: number;
    concepto: number;
    conceptoAdicional: number;
    user: null;
}

@Component({
    selector: "app-def-general",
    templateUrl: "./def-general.component.html",
    styleUrls: ["./def-general.component.css"],
})
export class DefGeneralComponent implements OnInit, OnDestroy {
    @Input() modelRecived: ProDefiniciones;
    private _user: string = this._userServices.userCurrent;
    public promocion: string = '';
    private subscriptions: Subscription = new Subscription();
    public breakpoint: number;
    public tasas: any[];
    public conceptos: any[];
    public calculate: any[];
    public aplicateTo: any[];
    public calculate2: any[];
    public mensualPer: any[];
    public formPrincipal: boolean = true;
    public selectTasa: boolean;
    public selectCocepto: boolean;
    public selectCocepto2: boolean;
    public loading: boolean;
    modelo: ProDefiniciones;

    claveTasa: string = "";

    constructor(
        private _fb: FormBuilder,
        private _swalService: SwalService,
        private _proDefServices: ProDefinicionesService,
        private _userServices: UserService,
        private _injectServices: InjectableService,
        private _tasasServices: TasasService,
        private _conceptosServices: ConceptosService,
        private location: Location,
        private sharedDataService: SharedDataService,
        private route: ActivatedRoute
    ) {
    }

    ngOnInit(): void {
        // Obtener promoción de la ruta
        const routeParams = this.route.snapshot.paramMap;
        const promocionFromRoute = routeParams.get('promocion');
        
        // Intentar obtener la promoción de diferentes fuentes en orden de prioridad
        if (promocionFromRoute) {
            this.promocion = promocionFromRoute;
        } else {
            // Intentar desde sessionStorage
            const savedPromocion = sessionStorage.getItem('selectedPromocion');
            if (savedPromocion) {
                try {
                    const promocionData = JSON.parse(savedPromocion);
                    this.promocion = promocionData.sPromocion || promocionData.promocion || '';
                } catch (error) {
                    console.error('Error al parsear promoción desde sessionStorage:', error);
                }
            }
            
            // Si aún no hay promoción, intentar desde localStorage (fallback)
            if (!this.promocion) {
                this.promocion = localStorage.getItem("prom") || '';
            }
        }

        // Actualizar el formulario con la promoción
        if (this.promocion) {
            this.form.patchValue({ promocion: this.promocion });
        }

        // Cargar datos desde sessionStorage si están disponibles
        this.cargarDatosDesdeSessionStorage();
        
        // Suscribirse al servicio compartido
        const sharedDataSub = this.sharedDataService.currentData$.subscribe((data: IDefGeneral) => {
            if (data && data.promocion) {
                // Actualizar promoción si viene del servicio
                if (!this.promocion) {
                    this.promocion = data.promocion;
                    this.form.patchValue({ promocion: this.promocion });
                }
                this.procesarDatosDefinicion(data);
            }
        });
        this.subscriptions.add(sharedDataSub);

        // Cargar datos iniciales
        this.readPeriodoMensual();
        this.readCalculate();
        this.readAplicateTo();
        this.readCalculate2();
        this.readConceptos();
        this.injectConcepto();
        this.readTasas();
        this.injectTasa();
        this.onBreakPonit();
    }

    ngOnDestroy(): void {
        // Limpiar todas las suscripciones
        this.subscriptions.unsubscribe();
    }

    private cargarDatosDesdeSessionStorage(): void {
        const savedPromocion = sessionStorage.getItem('selectedPromocion');
        if (savedPromocion) {
            try {
                const promocionData = JSON.parse(savedPromocion);
                // Convertir los datos de promoción al formato esperado por def-general
                const defGeneralData: IDefGeneral = {
                    promocion: promocionData.sPromocion,
                    numVar: 0, // Valor por defecto
                    descripcion: promocionData.descripcion,
                    claveTasa: '',
                    valorFijo: 0,
                    amortInf: 0,
                    amortSup: 0,
                    formula: '',
                    porcentaje: 0,
                    concepto: 0,
                    conceptoAdicional: 0,
                    user: null
                };
                this.procesarDatosDefinicion(defGeneralData);
            } catch (error) {
                console.error('Error al cargar datos desde sessionStorage:', error);
                sessionStorage.removeItem('selectedPromocion');
            }
        }
    }

    private procesarDatosDefinicion(data: IDefGeneral): void {
        // Solo procesar si data no es null y tiene las propiedades necesarias
        if (data && data.promocion) {
            // Verificar si realmente existe una definición (tiene formula y numVar válido)
            const tieneDefinicionValida = data.formula && data.formula.trim() !== '' && data.numVar && data.numVar > 0;
            
            // Actualizar el formulario con el numVar si existe
            if (data.numVar && data.numVar > 0) {
                this.form.patchValue({ numVar: data.numVar });
            }
            
            // Solo establecer modelRecived si hay una definición válida (modo edición)
            if (tieneDefinicionValida) {
                this.claveTasa = data.claveTasa || '';
                this.getFormula(data.formula);
                this.modelRecived = {
                    amortInf: data.amortInf || 0,
                    amortSup: data.amortSup || 0,
                    claveTasa: data.claveTasa || '',
                    concepto: data.concepto || 0,
                    conceptoAdicional: data.conceptoAdicional || 0,
                    descripcion: data.descripcion || '',
                    formula: data.formula || '',
                    numVar: data.numVar || 1,
                    porcentaje: data.porcentaje || 0,
                    promocion: data.promocion,
                    user: data.user || null,
                    valorFijo: data.valorFijo || 0,
                };
            } else {
                // Modo creación: limpiar modelRecived y solo usar numVar si existe
                this.modelRecived = null;
                if (data.claveTasa) {
                    this.claveTasa = data.claveTasa;
                    this.form.patchValue({ claveTasa: data.claveTasa });
                }
            }
        }
    }

    form = this._fb.group({
        promocion: [this.promocion],
        numVar: [
            1,
            {
                validators: [],
                asyncValidators: [this.singleDefinicion()],
                updateOn: "blur",
            },
        ],
        descripcion: [null],
        claveTasa: [null],
        claveTasa2: [null],
        valorFijo: ["0.0"],
        amortInf: [0],
        amortSup: [0],
        formula: [null],
        porcentaje: ["0.0"],
        concepto: [null],
        conceptoAdicional: [0],
        user: this._user,
        perdiodoMensual: [null],

        // Valores que van en formula
        calculate: [1],
        calculate2: [1],
        aplicateTo: [1],
        fecha: [null],
        meses: [null],
        dias: [null],
        ajustarDiaHabil: [null],
        frecuenciaPagos: [null],
        pagoInteresesMensual: [null],
        ivaCapitalList: [null],
        ivaCapital: [null],
        ivaInteresesList: [null],
        ivaIntereses: [null],
        firstPayment: [null],
        cobrarDiasExtras: [null],
        modificarPrecio: [null],
        valorResidualVal: [null],
        valorResidual: [null],
        pagosAnticipados: [null],
        modificarCuotas: [null],
        restarEnganche: [null],
        engancheVal: [null],
        enganche: [null],
        pagosPredefinidos: [null],
        tipoArmonizacion: [null],
        gradiente: [null],
        aplicacionPagos: [null],
        ventasAbono: [null],
        interesesSaldos: [null],
        pagoCapitalSinInteresTasaList: [null],
        pagoCapitalSinInteresTasa: [null],
        aplicacionTasa: [null],
        periodoSinCapitalList: [null],
        periodoSinCapital: [null],
        periodoCapitalSinInteresesList: [null],
        periodoCapitalSinIntereses: [null],
    });

    readPeriodoMensual() {
        let iTipo: number = 9;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.mensualPer = res.data;
        });
    }

    readCalculate() {
        let iTipo: number = 20;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.calculate = res.data;
        });
    }

    readAplicateTo() {
        let iTipo: number = 21;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.aplicateTo = res.data;
        });
    }

    readCalculate2() {
        let iTipo: number = 22;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.calculate2 = res.data;
        });
    }

    getModel() {
        // Usar numVar del formulario o del modelRecived si está en modo edición, o 1 por defecto
        const numVar = this.modelRecived?.numVar || this.form?.controls.numVar?.value || 1;
        this.modelo = {
            promocion: this.promocion,
            numVar: numVar,
            descripcion: "DEFINICION DE CALCULO DE GENERAL",
            formula: this.setFormula(),
            claveTasa: this.form?.controls.claveTasa.value,
            valorFijo: parseInt(this.form?.controls.valorFijo.value),
            amortInf: 0,
            amortSup: 0,
            porcentaje: 0,
            concepto: 0,
            conceptoAdicional: 0,
            user: this._user,
        };
        return this.modelo;
    }

    readConceptos() {
        this._conceptosServices.getAll().subscribe((res) => {
            this.conceptos = res;
        });
    }

    injectConcepto() {
        const conceptoSub = this._injectServices.disparadorConcepto.subscribe((res) => {
            if (this.selectCocepto2) this.form.patchValue({ conceptoAdicional: res.model.data });
            else this.form.patchValue({ concepto: res.model.data });
            if (res.model.success) this.changeBoolean("form");
        });
        this.subscriptions.add(conceptoSub);
    }

    readTasas() {
        this._tasasServices.read().subscribe((res) => {
            this.tasas = res.data;
        });
    }

    injectTasa() {
        const tasaSub = this._injectServices.disparadorTasas.subscribe((res) => {
            this.form.patchValue({ claveTasa: res.model.data });
            if (res.model.success) this.changeBoolean("form");
        });
        this.subscriptions.add(tasaSub);
    }

    changeBoolean(change: string) {
        switch (change) {
            case "form":
                this.selectCocepto = false;
                this.selectCocepto2 = false;
                this.selectTasa = false;
                this.formPrincipal = true;
                break;
            case "conceptos":
                this.formPrincipal = false;
                this.selectCocepto = true;
                break;
            case "tasas":
                this.formPrincipal = false;
                this.selectTasa = true;
                break;
            case "conceptos2":
                this.formPrincipal = false;
                this.selectCocepto = true;
                this.selectCocepto2 = true;
                break;
        }
    }

    create() {
        if (this.form.valid) {
            this.loading = true;
            //model = { ...model, formula: this.setFormula() };
            this._proDefServices.create(this.getModel()).subscribe((res) => {
                this.loading = false;
                if (res.exito === 1) {
                    // this._dialogRef.close();
                    console.log("Agregado");
                    this._swalService.success("Agregado");
                } else this._swalService.error("No Agregado");
            });
        }
    }

    update() {
        if (this.form.valid) {
            this.loading = true;
            // Incluir claveTasa y claveTasa2 del formulario para que se guarden los valores elegidos por el usuario
            const claveTasa = this.form.get('claveTasa')?.value;
            const claveTasa2 = this.form.get('claveTasa2')?.value;
            let model: any = {
                ...this.modelRecived,
                formula: this.setFormula(),
                claveTasa: claveTasa != null && claveTasa !== '' ? claveTasa : this.modelRecived?.claveTasa,
                claveTasa2: claveTasa2 != null && claveTasa2 !== '' ? claveTasa2 : undefined,
            };
            this._proDefServices.update(model).subscribe((res) => {
                this.loading = false;
                if (res.exito === 1) {
                    // this._dialogRef.close();
                    console.log("Editado");
                    this._swalService.success("Editado");
                } else this._swalService.error("No Editado");
            });
        }
    }

    formValue(value: string) {
        return this.form.get(value);
    }

    variableValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const variable = control.value;

            if (!variable) return;

            if (variable < 1 || variable > 1) return { variableValidation: true };

            return null;
        };
    }

    singleDefinicion(): AsyncValidatorFn {
        return (control: AbstractControl): Promise<ValidationErrors> | Observable<any> => {
            if (this.modelRecived) return of(null);
            
            // Validar que tenemos promoción antes de hacer la petición
            if (!this.promocion || this.promocion === 'null' || this.promocion === 'undefined') {
                console.warn('singleDefinicion: No hay promoción disponible para validar');
                return of(null); // Retornar válido si no hay promoción (evitar error)
            }
            
            return this._proDefServices.readOne(this.promocion, control.value).pipe(
                map((res) => {
                    if (res && res.exito === 1) return { singleDefinicion: true };
                    return null;
                })
            );
        };
    }

    setFormula(): string {
        let sep = "|";

        let lst =
            this.form.controls["aplicacionTasa"].value +
            sep +
            this.form.controls["tipoArmonizacion"].value +
            sep +
            this.form.controls["calculate"].value +
            sep +
            this.form.controls["frecuenciaPagos"].value +
            sep +
            this.form.controls["dias"].value +
            sep +
            this.form.controls["meses"].value +
            sep +
            this.form.controls["fecha"].value +
            sep +
            this.form.controls["ivaIntereses"].value +
            sep +
            this.form.controls["ivaInteresesList"].value +
            sep +
            "0" +  // Posición 9 - siempre 0
            sep +
            this.form.controls["ivaCapitalList"].value +
            sep +
            this.form.controls["ivaCapital"].value +
            sep +
            "0" +  // Posición 12 - siempre 0
            sep +
            "0" +  // Posición 13 - siempre 0
            sep +
            this.form.controls["perdiodoMensual"].value +
            sep +
            this.form.controls["ventasAbono"].value +
            sep +
            this.form.controls["aplicacionPagos"].value +
            sep +
            this.form.controls["claveTasa2"].value +
            sep +
            this.form.controls["enganche"].value +
            sep +
            this.form.controls["engancheVal"].value +
            sep +
            this.form.controls["modificarCuotas"].value +
            sep +
            this.form.controls["pagosAnticipados"].value +
            sep +
            this.form.controls["valorResidual"].value +
            sep +
            this.form.controls["valorResidualVal"].value +
            sep +
            this.form.controls["restarEnganche"].value +
            sep +
            this.form.controls["modificarPrecio"].value +
            sep +
            this.form.controls["cobrarDiasExtras"].value +
            sep +
            this.form.controls["ajustarDiaHabil"].value +
            sep +
            this.form.controls["pagosPredefinidos"].value +
            sep +
            this.form.controls["pagoCapitalSinInteresTasa"].value +
            sep +
            this.form.controls["pagoInteresesMensual"].value +
            sep +
            this.form.controls["periodoCapitalSinIntereses"].value +
            sep +
            this.form.controls["gradiente"].value +
            sep +
            this.form.controls["firstPayment"].value +
            sep +
            this.form.controls["interesesSaldos"].value +
            sep +
            this.form.controls["periodoSinCapital"].value +
            sep +
            this.form.controls["pagoCapitalSinInteresTasaList"].value +
            sep +
            this.form.controls["periodoCapitalSinInteresesList"].value +
            sep +
            this.form.controls["periodoSinCapitalList"].value;

        // Manejo especial para modificarPrecio antes de la conversión general
        const modificarPrecioValue = this.form.controls["modificarPrecio"].value;
        if (modificarPrecioValue === false) {
            // Reemplazar el valor de modificarPrecio con vacío antes de la conversión
            lst = lst.replace(this.form.controls["modificarPrecio"].value, "");
        }
        
        // Convertir true/false a 1/0 para el resto de campos
        lst = lst.replace(/true/g, "1").replace(/false/g, "0");

        return lst;
    }

    getFormula(formula: string): void {
        // Validar que la fórmula no esté vacía
        if (!formula || formula.trim() === '') {
            return;
        }
        
        let formulaSep: string[] = formula.split("|");
        //Valores que van fuera de fórmula
        this.form.controls["claveTasa"].patchValue(this.claveTasa);

        // Valores que van en formula
        this.form.controls["aplicacionTasa"].patchValue(parseInt(formulaSep[0]));
        this.form.controls["tipoArmonizacion"].patchValue(parseInt(formulaSep[1]));
        this.form.controls["calculate"].patchValue(parseInt(formulaSep[2]));
        this.form.controls["frecuenciaPagos"].patchValue(parseInt(formulaSep[3]));
        this.form.controls["dias"].patchValue(parseInt(formulaSep[4]));
        this.form.controls["meses"].patchValue(parseInt(formulaSep[5]));
        this.form.controls["fecha"].patchValue(parseInt(formulaSep[6]));
        this.form.controls["ivaIntereses"].patchValue(parseFloat(formulaSep[7]));
        this.form.controls["ivaInteresesList"].patchValue(parseInt(formulaSep[8]));
        // Posición 9 - siempre 0 (no se lee del formulario)
        this.form.controls["ivaCapitalList"].patchValue(parseInt(formulaSep[10]));
        this.form.controls["ivaCapital"].patchValue(parseFloat(formulaSep[11]));
        // Posición 12 - siempre 0 (no se lee del formulario)
        // Posición 13 - siempre 0 (no se lee del formulario)
        this.form.controls["perdiodoMensual"].patchValue(parseInt(formulaSep[14]));
        this.form.controls["ventasAbono"].patchValue(parseInt(formulaSep[15]));
        this.form.controls["aplicacionPagos"].patchValue(parseInt(formulaSep[16]));
        this.form.controls["claveTasa2"].patchValue(formulaSep[17]);
        this.form.controls["enganche"].patchValue(parseFloat(formulaSep[18]));
        this.form.controls["engancheVal"].patchValue(parseFloat(formulaSep[19]));
        this.form.controls["modificarCuotas"].patchValue(parseInt(formulaSep[20]));
        this.form.controls["pagosAnticipados"].patchValue(parseInt(formulaSep[21]));
        this.form.controls["valorResidual"].patchValue(parseFloat(formulaSep[22]));
        this.form.controls["valorResidualVal"].patchValue(parseFloat(formulaSep[23]));
        this.form.controls["restarEnganche"].patchValue(parseInt(formulaSep[24]));
        // modificarPrecio: vacío = false, 1 = true
        const modificarPrecioValue = formulaSep[25];
        this.form.controls["modificarPrecio"].patchValue(modificarPrecioValue === "1" ? true : false); 
        this.form.controls["cobrarDiasExtras"].patchValue(parseInt(formulaSep[26]));
        this.form.controls["ajustarDiaHabil"].patchValue(parseInt(formulaSep[27]));
        this.form.controls["pagosPredefinidos"].patchValue(parseInt(formulaSep[28]));
        this.form.controls["pagoCapitalSinInteresTasa"].patchValue(parseInt(formulaSep[29]));
        this.form.controls["pagoInteresesMensual"].patchValue(parseInt(formulaSep[30]));
        this.form.controls["periodoCapitalSinIntereses"].patchValue(parseInt(formulaSep[31]));
        this.form.controls["gradiente"].patchValue(formulaSep[32]);
        this.form.controls["firstPayment"].patchValue(formulaSep[33] === "1" ? true : false);
        this.form.controls["interesesSaldos"].patchValue(parseInt(formulaSep[34]));
        this.form.controls["periodoSinCapital"].patchValue(parseInt(formulaSep[35]));
        this.form.controls["pagoCapitalSinInteresTasaList"].patchValue(parseInt(formulaSep[36]));
        this.form.controls["periodoCapitalSinInteresesList"].patchValue(parseInt(formulaSep[37]));
        this.form.controls["periodoSinCapitalList"].patchValue(parseInt(formulaSep[38]));

    }

    onBreakPonit() {
        this.breakpoint = window.innerWidth <= 200 ? 1 : 2;
    }

    onResize(event) {
        this.breakpoint = event.target.innerWidth <= 200 ? 1 : 2;
    }

    close() {
        // this._dialogRef.close();
        this.location.back();
    }
}
